import { z } from "zod";
import { fieldUse, commonFields } from "./fieldUse.js";

const errorClass = 'error';
const feedbackClass = 'feedback';

const fieldSchemas = {
    enquiryType: z.string().min(1, "Please select an enquiry type."),
    fullName: z.string().min(1, "Full name is required."),
    email: z.string().email({ message: "Valid email is required." }),
    telephone: z.string().regex(/^\+?[0-9\s\-]+$/, "Invalid phone number.").optional().nullable().or(z.literal('')),
    message: z.string().min(1, "Message is required."),
    projectName: z.string().optional(),
    projectStage: z.string().optional(),
    filmTitleFestivalName: z.string().min(1, "Film title / festival name is required."),
    screeningOrSalesNeeds: z.string().min(1, "Please select a need."),
    productionTitle: z.string().min(1, "Production title is required."),
    expectedStartDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/, "Expected start date is required."),
    locationsOfInterest: z.string().optional(),
};

export const triggerFieldValidation = (input) => {
    const fieldName = input.name;
    const value = input.value;

    const result = validateField(fieldName, value);
    toggleErrorClasses(input, result);
}

const validateField = (fieldName, value) => {
    const schema = fieldSchemas[fieldName];

    if (!schema) return { success: true }; // No validation for this field

    return schema.safeParse(value);
};

const toggleErrorClasses = (field, result) => {
    const fieldName = field.getAttribute('name');

    if (!fieldSchemas[fieldName]) return; // Skip fields not in the schema

    const formControl = field.closest('.form-control');

    if (!formControl) return; // Skip fields with no wrapper

    const feedbackElement = formControl.querySelector(`.${feedbackClass}`) || document.createElement('div');
    feedbackElement.className = feedbackClass + ' text-error text-sm mt-1';

    if (!result.success) {
        formControl.classList.add(errorClass);

        // Extract and display error messages
        if (result.error) {
            const errorMessages = result.error.errors.map((error) => error.message).join(' ');
            feedbackElement.textContent = errorMessages;
        }

        if (!formControl.contains(feedbackElement)) {
            formControl.appendChild(feedbackElement);
        }
    } else {
        formControl.classList.remove(errorClass);
        feedbackElement.textContent = '';
    }
};

export const validateForm = (form, enquiryType) => {
    let isValid = true;
    const formData = new FormData(form);

    // Get fields to validate based on the selected enquiry type
    const fieldsToValidate = {
        enquiryType: "js-enquiry-type",
        ...commonFields,
        ...(fieldUse[enquiryType]?.extraFields || {}),
    };

    for (const [fieldName, value] of formData.entries()) {
        if (!fieldsToValidate[fieldName]) continue; // Skip fields not relevant to the enquiry type

        const field = form.querySelector(`[name="${fieldName}"]`);

        if (!field) continue;

        const result = validateField(fieldName, value);
        toggleErrorClasses(field, result);

        if (!result.success) isValid = false;
    }

    return isValid;
};

export const attachValidationListeners = (form) => {
    // Attach validation listeners to all input, textarea, and select elements
    const inputs = form.querySelectorAll('input, textarea, select');

    if (!inputs) return;

    inputs.forEach((input) => {
        input.addEventListener('input', () => triggerFieldValidation(input));
    });
};
